import { Box } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmActionDialog from "../../Components/ConfirmActionDialog";
import KenLoader from "../../GlobalComponents/KenLoader";
import { useToast } from "../../Helpers/Contexts/toastContext";
import { useAuthStore } from "../../store";
import {
  getAuctionWindow,
  getPlayersDetails,
  getTeamDetails,
  updateAuctionWindow,
  updatePlayerDetails,
} from "../../Utils/apiService";
import {
  calculateTeamStatus,
  formatToIndianCurrencyWords,
  getRoleType,
  mapHighestLevelToCategory,
  splitCurrencyValueAndUnit,
  validateTeamSelectionStatus,
} from "../../Utils/helperFunctions";
import {
  getAuctionRules,
  resolveCategoryWithFallback,
  resolveTeamDivision,
} from "../../Utils/auctionRules";
import BidPriceModal from "./components/BidPriceModal";
import ConfirmStartBidModal from "./components/ConfirmStartBidModal";
import DashboardToolbar from "./components/DashboardToolbar";
import InsufficiantFundModal from "./components/InsufficiantFundModal";
import PlayersTable from "./components/PlayersTable";
import TeamsTable from "./components/TeamsTable";
import AuctionWindowModal from "./components/AuctionWindowModal";
import { AppliedFilters, PlayerRecord, SortKey, TeamRecord } from "./types";

const Dashboard = () => {
  const { showToast } = useToast();
  const {
    triggerTeamDetailsUpdate,
    setSelectedPlayerData,
    clearLiveBidData,
    clearLiveTeamId,
    sendLiveTeamId,
    setTeamDetailsDivision,
  } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeView, setActiveView] = useState<"men" | "women" | "team">("men");
  const [teamDivisionView, setTeamDivisionView] = useState<"men" | "women">(
    "men",
  );
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerRecord | null>(
    null,
  );
  const [bidAmount, setBidAmount] = useState(200000);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [playerDetails, setPlayerDetails] = useState<PlayerRecord[]>([]);
  const [teamDetails, setTeamDetails] = useState<TeamRecord[]>([]);
  const [trigger, setTrigger] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: "asc" | "desc";
  } | null>(null);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [setFilter, setSetFilter] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const [filterMenu, setFilterMenu] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [playerToEdit, setPlayerToEdit] = useState<PlayerRecord | null>(null);
  const [insufficientFunds, setInsufficientFunds] = useState(false);
  const [uniqueSets, setUniqueSets] = useState<string[]>([]);
  const [, setKittyBidAmount] = useState(0);
  const [clearQueueDialogOpen, setClearQueueDialogOpen] = useState(false);
  const [auctionWindowModalOpen, setAuctionWindowModalOpen] = useState(false);
  const [auctionWindowLoading, setAuctionWindowLoading] = useState(false);
  const [auctionWindowSaving, setAuctionWindowSaving] = useState(false);
  const [auctionWindowId, setAuctionWindowId] = useState<string | null>(null);
  const [auctionWindowStartAt, setAuctionWindowStartAt] = useState<
    string | null
  >(null);
  const [auctionWindowEndAt, setAuctionWindowEndAt] = useState<string | null>(
    null,
  );

  const normalizeCategoryFilterValue = (value: string | null | undefined) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "senior player" || normalized === "senior players") {
      return "senior player";
    }
    return normalized;
  };
  // const [showTeamModal, setShowTeamModal] = React.useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [playersRes, teamsRes] = await Promise.all([
        getPlayersDetails(),
        getTeamDetails(),
      ]);

      if (playersRes?.data?.success) {
        const players: PlayerRecord[] = playersRes.data.data || [];
        setPlayerDetails(players);

        const allSets = players
          .map((player: PlayerRecord) => player.set)
          .filter((set): set is string => Boolean(set));
        const uniqueSetList = [...new Set(allSets)];
        setUniqueSets(uniqueSetList);

        if (teamsRes?.data?.success) {
          const teams: TeamRecord[] = teamsRes.data.data || [];
          const enrichedTeams = calculateTeamStatus(players, teams);
          setTeamDetails(enrichedTeams as TeamRecord[]);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [trigger]);

  const teamdetailsWithStatus = useCallback(
    (bid: number, player: unknown) => {
      const playerData = player as PlayerRecord | null;
      const activeRules = getAuctionRules(
        (playerData as { playerDetails?: PlayerRecord } | null)?.playerDetails
          ?.gender || playerData?.gender,
      );
      setTeamDetails((prev: TeamRecord[]) =>
        prev.map((team) => ({
          ...team,
          teamStatus: validateTeamSelectionStatus(
            team,
            player,
            playerDetails,
            activeRules,
            bid,
          ),
        })),
      );
    },
    [playerDetails],
  );

  const handleUnsold = () => {
    if (!selectedPlayer) return;
    const nameParts = selectedPlayer.name.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts?.slice(1).join(" ");
    const basePrice = formatToIndianCurrencyWords(selectedPlayer.basePrice);
    const playerDataForLiveAuction = {
      firstName,
      lastName,
      photo: selectedPlayer.photo,
      MCANo: selectedPlayer.MCANo,
      playerAge: selectedPlayer.playerAge,
      playerBirthDate: selectedPlayer.birthData,
      roleType: getRoleType(selectedPlayer),
      highest_level_played: selectedPlayer.recentCompetetionLevel,
      auctionResult: "Unsold",
      basePrice: basePrice,
      teamLogo: selectedTeam?.Logo_URL__c,
      teamName: selectedTeam?.Name,
      soldToLogo: selectedTeam?.Logo_URL__c,
      soldTo: selectedTeam?.Name,
      soldToId: selectedTeam?.Id,
      winningBid: bidAmount,
      playerDetails: selectedPlayer,
    };

    const updatePayload = {
      Auction_Result__c: "Unsold",
      Sold_To__c: null,
      Winning_Bid__c: null,
    };
    const recordId = selectedPlayer?.playerRegId;
    if (!recordId) {
      showToast("error", "Player record ID is missing");
      return;
    }
    updatePlayerDetails(recordId, updatePayload)
      .then(async (res) => {
        await triggerTeamDetailsUpdate();
        await clearLiveBidData();
        await setSelectedPlayerData(playerDataForLiveAuction);
        await setTrigger(!trigger);
        setModalOpen(false);
      })
      .catch((err) => {
        showToast("error", "Error Updating Player registration");
      });
  };

  const handleEditClick = (player: PlayerRecord) => {
    setPlayerToEdit(player);
    setConfirmModalOpen(true);
  };

  const handleConfirmStartBid = async () => {
    if (!playerToEdit || typeof playerToEdit.basePrice !== "number") return;
    const player = playerToEdit;
    const playerBasePrice = Number(player.basePrice);
    setConfirmModalOpen(false);

    const nameParts = player.name.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");
    const bidSplit = splitCurrencyValueAndUnit(bidAmount);
    const basePrice = formatToIndianCurrencyWords(playerBasePrice);
    const playerDataForLiveAuction = {
      firstName,
      lastName,
      photo: player.photo,
      MCANo: player.MCANo,
      playerAge: player.playerAge,
      playerBirthDate: player?.birthData,
      role: player.role,
      roleType: getRoleType(player),
      highest_level_played: player.recentCompetetionLevel,
      auctionResult: "Stats",
      bidAmount: bidSplit.value,
      bidUnit: bidSplit.unit,
      basePrice: basePrice,
      teamLogo: selectedTeam?.Logo_URL__c,
      teamName: selectedTeam?.Name,
      playerDetails: player,
    };

    await triggerTeamDetailsUpdate();
    await clearLiveBidData();
    await setSelectedPlayerData(playerDataForLiveAuction);
    teamdetailsWithStatus(playerBasePrice, playerDataForLiveAuction);
    setSelectedPlayer(player);
    setBidAmount(playerBasePrice);
    setModalOpen(true);
  };

  const handleFinalize = () => {
    setModalOpen(false);
    // setShowTeamModal(true);
  };

  const handleTeamConfirm = async () => {
    if (!selectedTeam || !selectedPlayer) return;
    const liveStatus = validateTeamSelectionStatus(
      selectedTeam,
      selectedPlayer,
      playerDetails,
      getAuctionRules(selectedPlayer.gender),
      bidAmount,
    );

    if (!liveStatus?.valid) {
      showToast("error", liveStatus?.message || "Team is not eligible");
    }
    const validatedKittyBidAmount = Number(
      liveStatus?.valid ? liveStatus?.kittyBidAmount || 0 : 0,
    );
    setKittyBidAmount(validatedKittyBidAmount);

    const nameParts = selectedPlayer.name.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");
    const bidSplit = splitCurrencyValueAndUnit(bidAmount);
    const basePrice = formatToIndianCurrencyWords(selectedPlayer.basePrice);

    const updatePayload = {
      Winning_Bid__c: bidAmount,
      Sold_To__c: selectedTeam?.Id,
      Kitty_Bid_Amount__c: validatedKittyBidAmount,
      Reserve_Bid_Amount__c: bidAmount - validatedKittyBidAmount,
      Auction_Result__c: "Sold",
    };
    const playerDataForLiveAuction = {
      firstName,
      lastName,
      photo: selectedPlayer.photo,
      MCANo: selectedPlayer.MCANo,
      playerAge: selectedPlayer.playerAge,
      playerBirthDate: selectedPlayer?.birthData,
      roleType: getRoleType(selectedPlayer),
      highest_level_played: selectedPlayer.recentCompetetionLevel,
      auctionResult: "Sold",
      bidAmount: bidSplit.value,
      bidUnit: bidSplit.unit,
      basePrice: basePrice,
      teamLogo: selectedTeam?.Logo_URL__c,
      teamName: selectedTeam?.Name,
      playerDetails: selectedPlayer,
    };

    const recordId = selectedPlayer?.playerRegId;
    if (!recordId) {
      showToast("error", "Player record ID is missing");
      return;
    }
    updatePlayerDetails(recordId, updatePayload)
      .then(async (res) => {
        await triggerTeamDetailsUpdate();
        await setSelectedPlayerData(playerDataForLiveAuction);
        await clearLiveBidData();
        setTrigger(!trigger);
        setModalOpen(false);
        // setShowTeamModal(false);
        setSelectedTeam(null);
      })
      .catch(() => {
        showToast("error", "Error Updating Player registration");
      });
  };

  const handleClearQueueConfirm = () => {
    useAuthStore.getState().clearSelectedPlayerData();
    clearLiveBidData();
    clearLiveTeamId();
    setClearQueueDialogOpen(false);
  };

  const filteredPlayers = useMemo(() => {
    return playerDetails.filter((player: PlayerRecord) => {
      const playerGender = (player.gender || "").toLowerCase();
      const matchesViewTab =
        activeView === "women"
          ? playerGender === "female"
          : playerGender === "male";
      const activeDivision = activeView === "women" ? "women" : "men";

      const term = searchTerm?.toLowerCase();
      const matchesSearch =
        player?.name?.toLowerCase().includes(term) ||
        player?.auctionNumber?.toString() === term;

      const matchesRole =
        !appliedFilters.role || player.role === appliedFilters.role;
      const matchesStatus =
        !appliedFilters.status ||
        (appliedFilters.status === "Sold" && player.auctionResult === "Sold") ||
        (appliedFilters.status === "Unsold" &&
          player.auctionResult === "Unsold") ||
        (appliedFilters.status === "Pending" && !player.auctionResult);

      const matchesCategory =
        !appliedFilters.category ||
        normalizeCategoryFilterValue(
          resolveCategoryWithFallback({
            division: activeDivision,
            basePrice: player?.basePrice,
            mcaNo: player?.MCANo,
            fallbackCategory: mapHighestLevelToCategory(
              player.recentCompetetionLevel || "",
            ),
            playerName: player?.name,
            playerAge: player.playerAge,
            birthData: player.birthData,
          }),
        ) === normalizeCategoryFilterValue(appliedFilters.category);

      const matchesSet =
        !appliedFilters.set || player.set === appliedFilters.set;

      return (
        matchesViewTab &&
        matchesSearch &&
        matchesRole &&
        matchesStatus &&
        matchesCategory &&
        matchesSet
      );
    });
  }, [activeView, appliedFilters, playerDetails, searchTerm]);

  const sortedPlayers = useMemo(() => {
    if (!sortConfig) return filteredPlayers;
    const sorted = [...filteredPlayers].sort((a, b) => {
      const valA = String(a[sortConfig.key] ?? "").toLowerCase();
      const valB = String(b[sortConfig.key] ?? "").toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredPlayers, sortConfig]);

  const filteredTeams = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return teamDetails.filter((team) => {
      const matchesSearch = (team.Name || "").toLowerCase().includes(term);
      const isWomenTeam = String(team.Team_Type__c || "")
        .toLowerCase()
        .includes("women");
      const matchesDivision =
        teamDivisionView === "women" ? isWomenTeam : !isWomenTeam;
      return matchesSearch && matchesDivision;
    });
  }, [searchTerm, teamDetails, teamDivisionView]);

  const eligibleTeamsForSelectedPlayer = useMemo(() => {
    if (!selectedPlayer) return teamDetails;
    const selectedDivision =
      selectedPlayer.gender?.toLowerCase() === "female" ? "women" : "men";
    return teamDetails.filter(
      (team) =>
        resolveTeamDivision(team as Record<string, unknown>) ===
        selectedDivision,
    );
  }, [selectedPlayer, teamDetails]);

  const handleSortChange = (key: SortKey) => {
    setSortConfig((prev) =>
      prev?.key === key
        ? {
            key,
            direction: prev.direction === "asc" ? "desc" : "asc",
          }
        : { key, direction: "asc" },
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(0);
  };

  const handleViewChange = (view: "men" | "women" | "team") => {
    setActiveView(view);
    if (view === "men" || view === "women") {
      setTeamDetailsDivision(view);
      triggerTeamDetailsUpdate();
    }
    setPage(0);
  };

  const handleTeamDivisionChange = (view: "men" | "women") => {
    setTeamDivisionView(view);
    setPage(0);
  };

  const handleOpenAuctionWindow = async () => {
    setAuctionWindowModalOpen(true);
    setAuctionWindowLoading(true);
    try {
      const res = await getAuctionWindow();
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      const currentWindow = rows[0];
      if (!currentWindow?.Id) {
        showToast("error", "Auction window not found");
        setAuctionWindowId(null);
        setAuctionWindowStartAt(null);
        setAuctionWindowEndAt(null);
        return;
      }
      setAuctionWindowId(currentWindow.Id);
      setAuctionWindowStartAt(
        currentWindow.Auction_Start_Date_Time__c
          ? String(currentWindow.Auction_Start_Date_Time__c)
          : null,
      );
      setAuctionWindowEndAt(
        currentWindow.Auction_End_Date_Time__c
          ? String(currentWindow.Auction_End_Date_Time__c)
          : null,
      );
    } catch (error) {
      console.error("Error fetching auction window:", error);
      showToast("error", "Unable to fetch auction window");
    } finally {
      setAuctionWindowLoading(false);
    }
  };

  const handleSaveAuctionWindow = async (payload: {
    Auction_Start_Date_Time__c: string;
    Auction_End_Date_Time__c: string;
  }) => {
    if (!auctionWindowId) {
      showToast("error", "Auction window id not found");
      return;
    }
    try {
      setAuctionWindowSaving(true);
      await updateAuctionWindow(auctionWindowId, payload);
      setAuctionWindowStartAt(payload.Auction_Start_Date_Time__c);
      setAuctionWindowEndAt(payload.Auction_End_Date_Time__c);
      await triggerTeamDetailsUpdate();
      showToast("success", "Auction window updated successfully");
    } catch (error) {
      console.error("Error updating auction window:", error);
      showToast("error", "Unable to update auction window");
    } finally {
      setAuctionWindowSaving(false);
    }
  };

  return loading ? (
    <KenLoader />
  ) : (
    <Box
      color="#fff"
      sx={{
        position: "relative",
        height: "95%",
        minHeight: 0,
        p: { xs: 1, md: "0.8vw" },
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.12)",
        background: "linear-gradient(180deg, #0f1015 0%, #0b0c11 100%)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <DashboardToolbar
        activeView={activeView}
        onViewChange={handleViewChange}
        teamDivisionView={teamDivisionView}
        onTeamDivisionChange={handleTeamDivisionChange}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filteredCount={
          activeView === "team" ? filteredTeams.length : filteredPlayers.length
        }
        appliedFilters={appliedFilters}
        roleFilter={roleFilter}
        statusFilter={statusFilter}
        categoryFilter={categoryFilter}
        setFilter={setFilter}
        uniqueSets={uniqueSets}
        filterMenu={filterMenu}
        setRoleFilter={setRoleFilter}
        setStatusFilter={setStatusFilter}
        setCategoryFilter={setCategoryFilter}
        setSetFilter={setSetFilter}
        setAppliedFilters={setAppliedFilters}
        setFilterMenu={setFilterMenu}
        onAuctionWindowOpen={handleOpenAuctionWindow}
        onClearQueue={() => setClearQueueDialogOpen(true)}
      />

      {activeView === "team" ? (
        <TeamsTable
          filteredCount={filteredTeams.length}
          page={page}
          rowsPerPage={rowsPerPage}
          teams={filteredTeams}
          onTeamClick={sendLiveTeamId}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      ) : (
        <PlayersTable
          activeView={activeView}
          filteredCount={filteredPlayers.length}
          page={page}
          rowsPerPage={rowsPerPage}
          sortConfig={sortConfig}
          sortedPlayers={sortedPlayers}
          onEditClick={handleEditClick}
          onSendLiveTeamId={sendLiveTeamId}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      <ConfirmStartBidModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmStartBid}
        player={playerToEdit}
      />
      <BidPriceModal
        open={modalOpen}
        onClose={() => {
          useAuthStore.getState().clearSelectedPlayerData();
          useAuthStore.getState().clearLiveBidData();
          setModalOpen(false);
        }}
        player={selectedPlayer}
        bid={bidAmount}
        setBid={setBidAmount}
        onFinalize={handleFinalize}
        onUnsold={handleUnsold}
        teams={eligibleTeamsForSelectedPlayer}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
        onConfirm={handleTeamConfirm}
        teamdetailsWithStatus={teamdetailsWithStatus}
      />
      {/* <TeamSelectModal
        open={showTeamModal}
        // onClose={() => {
        //   setShowTeamModal(false);
        // }}
        bidPrice={bidAmount}
        teams={teamDetails}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
        onConfirm={handleTeamConfirm}
        onGoBack={() => {
          setShowTeamModal(false);
          setModalOpen(true);
        }}
        validateTeam={validateTeamSelection}
      /> */}
      <InsufficiantFundModal
        open={insufficientFunds}
        onClose={() => setInsufficientFunds(false)}
      />
      <ConfirmActionDialog
        open={clearQueueDialogOpen}
        onClose={() => setClearQueueDialogOpen(false)}
        onConfirm={handleClearQueueConfirm}
        title="Clear Live Queue"
        message="Are you sure you want to clear the live auction queue?"
        confirmText="Clear"
        cancelText="Cancel"
      />
      <AuctionWindowModal
        open={auctionWindowModalOpen}
        loading={auctionWindowLoading}
        saving={auctionWindowSaving}
        startAt={auctionWindowStartAt}
        endAt={auctionWindowEndAt}
        onClose={() => setAuctionWindowModalOpen(false)}
        onSave={handleSaveAuctionWindow}
      />
    </Box>
  );
};

export default Dashboard;
